<script setup>
import ExportCustomersAudienceAction from '@frontJs/components/actions/projectManagement/sea/ExportCustomersAudienceAction.vue';
import SynchronizeAudienceAction from '@frontJs/components/actions/projectManagement/sea/SynchronizeAudienceAction.vue';
import ArrowsClockwiseIcon from '@iconify-vue/ph/arrows-clockwise';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'customerTypeCategory',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Catégorie type client',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.customerTypeCategory.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'total',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Total',
                }),
            cell: (props) =>
                props.row.original.total
                    ? h(NumberCell, {
                          number: props.row.original.total,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'synchronizedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Synchronisé le',
                }),
            cell: (props) =>
                props.row.original.synchronizedAt
                    ? h(TextCell, {
                          text: formatDate(props.row.original.synchronizedAt),
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sea.audience',
                                    {
                                        tenant: tenant.id,
                                        googleAdsAudienceId:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                            h(
                                SynchronizeAudienceAction,
                                {
                                    googleAdsAudience: props.row.original,
                                    tenant,
                                    callback: updateData,
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Synchroniser',
                                            icon: ArrowsClockwiseIcon,
                                            callback: action,
                                            loading,
                                        }),
                                },
                            ),
                            h(
                                ExportCustomersAudienceAction,
                                {
                                    googleAdsAudience: props.row.original,
                                    tenant,
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            type: 'secondary',
                                            title: 'Exporter les clients',
                                            icon: DownloadSimpleIcon,
                                            callback: action,
                                            loading,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sea.postAudiences', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.sea.audience', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
